import styled, { css } from 'styled-components';

import { Colors } from 'modules/ui/theme/colors';

export const PageContainer = styled.div`
  background-color: ${Colors.White};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 40px;
  overflow-y: auto;
`;

export const LogoImage = styled.img`
  width: 105px;
  margin-bottom: 32px;
`;

export const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: ${Colors.Black};
  margin-bottom: 8px;
`;

export const Subtitle = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${Colors.SolidGray};
  margin-bottom: 32px;
`;

export const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

export const ToggleLabel = styled.span<{ active: boolean }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ active }): string => (active ? Colors.Black : Colors.SolidGray)};
  transition: color 0.2s ease;
`;

export const SaveBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.Secondary};
  margin-left: 4px;
`;

export const CardsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  width: 100%;
`;

export const TogglePill = styled.span<{ active: boolean }>`
  display: inline-block;
  width: 44px;
  height: 24px;
  background-color: ${({ active }): string =>
    active ? Colors.Primary : '#E8EAF1'};
  border-radius: 12px;
  position: relative;
  transition: background-color 0.25s linear;

  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: #ffffff;
    top: 3px;
    left: ${({ active }): string => (active ? '23px' : '3px')};
    transition: left 0.25s ease-in-out;
  }
`;

export const PlanCard = styled.div<{ selected: boolean; disabled: boolean }>`
  width: 200px;
  border: 2px solid
    ${({ selected }): string => (selected ? Colors.Primary : Colors.Gray)};
  border-radius: 12px;
  padding: 28px 24px;
  cursor: ${({ disabled }): string => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }): number => (disabled ? 0.55 : 1)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  background-color: ${Colors.White};

  ${({ selected }): ReturnType<typeof css> | string =>
    selected
      ? css`
          box-shadow: 0 0 0 1px ${Colors.Primary};
        `
      : ''}

  &:hover {
    border-color: ${({ selected, disabled }): string =>
      disabled ? Colors.Gray : selected ? Colors.Primary : Colors.DarkGray};
  }
`;

export const PlanHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

export const PlanName = styled.h2`
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: ${Colors.Black};
`;

export const SelectedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  color: ${Colors.White};
  background-color: ${Colors.Primary};
  padding: 2px 8px;
  border-radius: 10px;
`;

export const CurrentBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  color: ${Colors.SolidGray};
  background-color: ${Colors.Gray};
  padding: 2px 8px;
  border-radius: 10px;
`;

export const PlanPrice = styled.div`
  margin-bottom: 20px;
`;

export const PriceAmount = styled.span`
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: ${Colors.Black};
`;

export const PricePeriod = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${Colors.SolidGray};
  margin-left: 4px;
`;

export const YearlySavings = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: ${Colors.Secondary};
  margin-top: 4px;
`;

export const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  color: ${Colors.Black};
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CheckIcon = styled.span`
  color: ${Colors.Secondary};
  font-size: 14px;
  line-height: 20px;
  flex-shrink: 0;
`;

export const PaymentSection = styled.div`
  width: 100%;
  max-width: 584px;
  margin-bottom: 24px;
`;

export const PaymentLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${Colors.Black};
  margin-bottom: 8px;
`;

export const CardElementWrapper = styled.div`
  border: 1px solid ${Colors.Gray};
  border-radius: 8px;
  padding: 12px 14px;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: ${Colors.Primary};
  }
`;

export const CardError = styled.p`
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.Destructive};
  margin-top: 8px;
  margin-bottom: 0;
`;
